import { FC, useMemo } from "react";

import {
  CampaignObjectDetectorEnum,
  CampaignType,
} from "@vviinn/user-managment-client-ts";

import {
  availabilitySwitchableSettings,
  categoryFilterTypeSettings,
  categoryImpactNumericSettings,
  categorySensitivityTypeSettings,
  controlledInputTitles,
  influencersAttributesSwitchableSettings,
  numericModalSettings,
  percentageNumberSettings,
  priceImpactNumericSettings,
  selectSettingsTitles,
  similarityNumericSettings,
  textCampaignInfluencersKeys,
} from "../../constants";

import { Restricted } from "../../../components/Restricted";
import { StateAction } from "../../../helpers/functions/types";
import { parseNumberOrZero } from "../../../helpers";

import {
  NewCampaignInterfaceControls,
  RenderSwitchableSettings,
} from "./index";
import { CampaignCommonProps } from "../types";
import { MISSING_NET_TEXT } from "../constants";
import {
  HandleCampaignChange,
  NumericSettingsTitles,
  RelevantNets,
} from "../../types";
import { netTitleToSubtitle } from "../../../helpers/titles/netTitleToSubtitle";
import { NamedSelect } from "../../../ui/layout/molecules/NamedSelect";
import { NamedNumberic } from "../../../ui/layout/molecules/NamedNumberic";
import { typecastedTextSearch } from "../../textSearch/typecastedTextSearch";
import { isCampaignType } from "../helpers/isCampaignType";
import { ObjectDetectionCollapsedFields } from "../../components/ObjectDetection/ObjectDetectionCollapsedFields";
import { IN_DEVELOPMENT } from "../../../ui/constants";
import { InfoRow, SubSectionWithStyles } from "../../../ui/layout";

export const AdvancedNotVPDSettings: FC<
  {
    campaignType: Exclude<CampaignType, "VPD">;
    objectDetectorValue?: CampaignObjectDetectorEnum;
    setObjectDetectorValue: StateAction<CampaignObjectDetectorEnum | undefined>;
    relevantNets: RelevantNets;
  } & CampaignCommonProps
> = ({
  campaignType,
  currentValues,
  updateBodyField,
  objectDetectorValue,
  setObjectDetectorValue,
  relevantNets,
}) => {
  const { isTextSearchCampaign, isVprOrVpcCampaign } = useMemo(
    () => isCampaignType(campaignType),
    [campaignType],
  );

  const { relevantSimilarities, relevantColors, relevantTextNets } =
    relevantNets;

  const renderNumericSettings = (
    key: keyof NumericSettingsTitles,
    addSimilarity?: boolean,
    addPercentage?: boolean,
    subtitle?: string,
  ) => {
    return (
      <NamedNumberic
        addPercentage={addPercentage}
        addSimilarity={addSimilarity}
        title={controlledInputTitles[key]}
        key={key}
        subtitle={subtitle}
        value={currentValues[key]}
        {...numericModalSettings[key]}
        onChange={(target) =>
          updateBodyField({ [key]: parseNumberOrZero(target.value) })
        }
      />
    );
  };

  const handleObjectDetection: HandleCampaignChange =
    (key, isNumberSettings) => (value) => {
      const newValue = isNumberSettings
        ? parseNumberOrZero(String(value))
        : value;
      updateBodyField({
        [key]: newValue,
      });
    };

  return (
    <>
      <SubSectionWithStyles title="Semantic search" isSubHeader={false}>
        {!relevantTextNets?.length && !relevantSimilarities?.length ? (
          <InfoRow infoText={MISSING_NET_TEXT} />
        ) : (
          <>
            {relevantTextNets?.map((net) => {
              const { parentKey, typecastedEntity, typecastedKey, value } =
                typecastedTextSearch(net, currentValues);
              return (
                <NamedNumberic
                  key={net}
                  title={controlledInputTitles[net]}
                  subtitle={netTitleToSubtitle(net)}
                  value={value}
                  onChange={(target) =>
                    updateBodyField({
                      [parentKey]: {
                        ...typecastedEntity,
                        [typecastedKey]: parseNumberOrZero(target.value),
                      },
                    })
                  }
                  {...percentageNumberSettings}
                />
              );
            })}
            {relevantSimilarities?.map((net) =>
              renderNumericSettings(net, true, true, netTitleToSubtitle(net)),
            )}
            {similarityNumericSettings.map((key) => renderNumericSettings(key))}
          </>
        )}
      </SubSectionWithStyles>

      {!isTextSearchCampaign && (
        <>
          <SubSectionWithStyles title="Color Impact" isSubHeader={false}>
            {relevantColors?.length ? (
              relevantColors.map((net) =>
                renderNumericSettings(
                  net,
                  false,
                  true,
                  netTitleToSubtitle(net),
                ),
              )
            ) : (
              <InfoRow infoText={MISSING_NET_TEXT} />
            )}
          </SubSectionWithStyles>

          {isVprOrVpcCampaign && (
            <>
              <SubSectionWithStyles title="Price Impact" isSubHeader={false}>
                {priceImpactNumericSettings.map((key) =>
                  renderNumericSettings(key),
                )}
              </SubSectionWithStyles>
              <SubSectionWithStyles title="Category Impact" isSubHeader={false}>
                {categoryImpactNumericSettings.map((key) =>
                  renderNumericSettings(key),
                )}
                <NamedSelect
                  title={selectSettingsTitles.categorySensitivityColumn}
                  customId="categorySensitivityColumn"
                  options={categorySensitivityTypeSettings}
                  styleVariant="newPage"
                />
              </SubSectionWithStyles>
            </>
          )}
        </>
      )}
      <SubSectionWithStyles title="Availability" isSubHeader={false}>
        {availabilitySwitchableSettings.map((key) =>
          RenderSwitchableSettings(key, { currentValues, updateBodyField }),
        )}
      </SubSectionWithStyles>

      {objectDetectorValue && !isTextSearchCampaign && currentValues && (
        <SubSectionWithStyles title="Object Detection" isSubHeader={false}>
          <ObjectDetectionCollapsedFields
            objectDetector={objectDetectorValue}
            setObjectDetector={setObjectDetectorValue}
            handleChange={handleObjectDetection}
            campaign={currentValues}
            isModal={false}
          />
        </SubSectionWithStyles>
      )}
      {!isTextSearchCampaign && (
        <SubSectionWithStyles title="Products" isSubHeader={false}>
          <NamedSelect
            title={selectSettingsTitles.categoryFilterType}
            options={categoryFilterTypeSettings}
            customId="categoryFilterTypeSettings"
            styleVariant="newPage"
          />
        </SubSectionWithStyles>
      )}

      <NewCampaignInterfaceControls
        currentValues={currentValues}
        updateBodyField={updateBodyField}
        isTextSearchCampaign={isTextSearchCampaign}
      />
      {campaignType !== "VPS" && (
        <SubSectionWithStyles title="Influencers Filters" isSubHeader={false}>
          {campaignType !== "TEXT_SEARCH" ? (
            influencersAttributesSwitchableSettings.map((key) =>
              RenderSwitchableSettings(key, { currentValues, updateBodyField }),
            )
          ) : (
            <Restricted permissions={IN_DEVELOPMENT}>
              {textCampaignInfluencersKeys.map((key) =>
                RenderSwitchableSettings(key, {
                  currentValues,
                  updateBodyField,
                }),
              )}
            </Restricted>
          )}
        </SubSectionWithStyles>
      )}
    </>
  );
};
